import { motion } from "framer-motion";
import { MapPin, ArrowUpRight } from "lucide-react";

const CARLTON_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_investment-showcase-4/artifacts/b4db8qv4_WhatsApp%20Image%202026-04-07%20at%201.28.54%20PM.jpeg";

const STATS = [
  { value: "22", suffix: "yrs", label: "PK Properties in the UAE" },
  { value: "7", suffix: "yrs", label: "Carlton in the UAE market" },
  { value: "2", suffix: "Emirates", label: "Dubai & Abu Dhabi licenses" },
  { value: "1", suffix: "island", label: "Owners living on Hudayriyat" },
];

const TRACK = [
  { project: "Al Naseem", role: "Direct developer & resale" },
  { project: "Bashayer Villas", role: "Direct developer & resale" },
  { project: "Hudayriyat Golf Estates", role: "Live launch / current" },
];

const CREDS = [
  {
    title: "One of Dubai's first brokerages",
    body: "Established 22 years ago, among the original brokerages and holiday-homes operators in the city. A separate license operates in Abu Dhabi.",
  },
  {
    title: "Seven years across Dubai & Abu Dhabi",
    body: "Carlton has worked the UAE market for seven years, handling some of the highest-profile sales and rentals across both Dubai and Abu Dhabi. This was preceded by eight years advising developers and property investors across the United Kingdom.",
  },
  {
    title: "On the inside of Modon's island",
    body: "The last twelve months have been spent advising Abu Dhabi developers on project planning, sales and marketing, including the developments now coming to market on Hudayriyat.",
  },
  {
    title: "We bought here too",
    body: "We acquired a 5-bedroom contemporary in Al Naseem. We will be living on Hudayriyat and taking a physical office on the island, so we're permanently accessible after you move in.",
  },
];

export default function WhyUs() {
  return (
    <section
      id="why-us"
      data-testid="whyus-section"
      className="relative bg-[#1A3626] text-[#F4F2EC] py-24 md:py-36 overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 items-end mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-8">
            <div className="overline mb-5" style={{ color: "#B3906A" }}>
              Why PK Properties
            </div>
            <h2
              className="font-serif leading-[1.02] tracking-tight"
              style={{ fontSize: "clamp(40px, 5.5vw, 78px)" }}
            >
              We own where
              <br />
              <span className="italic font-light">we sell.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4">
            <p className="text-[#F4F2EC]/75 font-light leading-relaxed">
              The case for working with a 22-year UAE brokerage whose
              principals own on the island, advise the developer, and will be
              your neighbours.
            </p>
          </div>
        </div>

        {/* Portrait + bio split */}
        <div className="grid grid-cols-12 gap-6 md:gap-12 items-start mb-20 md:mb-28">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-5"
          >
            <div className="aspect-[4/5] overflow-hidden bg-[#F4F2EC]/10">
              <img
                src={CARLTON_PORTRAIT}
                alt="Carlton Hammond, PK Properties"
                className="w-full h-full object-cover"
                data-testid="carlton-portrait"
              />
            </div>
            <div className="mt-6 flex items-baseline justify-between border-b border-[#F4F2EC]/15 pb-5">
              <div>
                <div
                  className="overline"
                  style={{ color: "#B3906A" }}
                >
                  Managing Director
                </div>
                <div className="mt-2 font-serif text-3xl md:text-4xl">
                  Carlton Hammond
                </div>
              </div>
              <div className="text-right text-[11px] tracking-[0.22em] uppercase text-[#F4F2EC]/55">
                PK Properties
                <br />
                Est. 2004
              </div>
            </div>
            <div className="mt-5 flex items-center gap-2 text-[#F4F2EC]/65 text-sm">
              <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span>Future resident, Hudayriyat Island</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="col-span-12 md:col-span-7"
          >
            <p className="font-serif text-2xl md:text-3xl leading-snug text-[#F4F2EC]/95 mb-10">
              "We bought a 5-bedroom contemporary in Al Naseem. We've sold
              direct developer stock and resale across Al Naseem, Bashayer
              Villas and now Golf Estates. We're not here for a launch. We're
              here for the next decade."
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-10 gap-x-10">
              {CREDS.map((c, i) => (
                <div
                  key={c.title}
                  data-testid={`cred-${i}`}
                  className="border-t border-[#F4F2EC]/15 pt-6"
                >
                  <div className="text-[#B3906A] font-serif text-lg italic">
                    {String(i + 1).padStart(2, "0")}.
                  </div>
                  <h4 className="mt-2 font-serif text-xl md:text-2xl text-[#F4F2EC] leading-tight">
                    {c.title}
                  </h4>
                  <p className="mt-3 text-[#F4F2EC]/70 font-light leading-relaxed text-sm">
                    {c.body}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Stat ticker */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#F4F2EC]/15 border-y border-[#F4F2EC]/15 mb-20 md:mb-28">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              data-testid={`whyus-stat-${i}`}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-[#1A3626] p-7 md:p-10"
            >
              <div
                className="font-serif leading-[1] tracking-tight"
                style={{ fontSize: "clamp(48px, 5.5vw, 88px)" }}
              >
                {s.value}
                <span className="text-[#B3906A] text-2xl md:text-3xl ml-1">
                  {s.suffix}
                </span>
              </div>
              <div className="mt-3 text-[11px] tracking-[0.22em] uppercase text-[#F4F2EC]/60">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Track record */}
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <div
              className="overline mb-4"
              style={{ color: "rgba(244,242,236,0.55)" }}
            >
              Hudayriyat Track Record
            </div>
            <h3
              className="font-serif leading-[1.05]"
              style={{ fontSize: "clamp(32px, 3.6vw, 52px)" }}
            >
              Three precincts.
              <br />
              <span className="italic font-light">One island.</span>
            </h3>
          </div>
          <div className="col-span-12 md:col-span-8 md:pt-2">
            <ul className="border-y border-[#F4F2EC]/15">
              {TRACK.map((t, i) => (
                <motion.li
                  key={t.project}
                  data-testid={`track-${i}`}
                  initial={{ opacity: 0, x: 12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="flex items-center justify-between py-6 border-b border-[#F4F2EC]/15 last:border-0"
                >
                  <span className="font-serif text-xl md:text-2xl">
                    {t.project}
                  </span>
                  <span className="text-[#F4F2EC]/60 text-xs tracking-[0.2em] uppercase">
                    {t.role}
                  </span>
                </motion.li>
              ))}
            </ul>
            <p className="mt-8 text-[#F4F2EC]/70 font-light leading-relaxed max-w-2xl">
              Who else would you want to help you buy, sell, rent or
              holiday-let your unit than a long-established UAE company whose
              owners live in the community, and have a physical office
              minutes from your front door?
            </p>
            <a
              href="#contact"
              data-testid="whyus-cta"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById("contact");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="hge-btn-light mt-8 w-fit inline-flex"
            >
              Speak with Carlton
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
